import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {Address} from 'types/order';
import {createCard} from 'types/order';
import styles from './CardInformation.module.scss';
import Typography from 'components/Typography';
import Button from 'components/Button';
import Modal from 'components/Modal';
import {useForm} from 'react-hook-form';
import useUserForm from '../useUserForm';
import toast from 'react-hot-toast';
import {get} from 'lodash';
import closeIcon from '../../RouteHistory/components/Ticket/close.svg';
import axios from 'axios';
import useAuth from 'hooks/useAuth';
import {useHistory} from 'react-router-dom';
import DeleteModal from 'components/DeleteModal';
import {getCardType} from 'helpers/getCardTypeIcon';
import {CreditCardType} from 'components/CreditCardType';
import {format, parseISO} from 'date-fns';
import StripeForm from './StripeForm';
import HeartlandForm from './HeartlandForm';
import {loadStripe} from '@stripe/stripe-js';
import {Elements} from '@stripe/react-stripe-js';

export function cc_format(value: string) {
  var v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
  var matches = v.match(/\d{4,16}/g);
  var match = (matches && matches[0]) || '';
  var parts = [];

  for (let i = 0, len = match.length; i < len; i += 4) {
    parts.push(match.substring(i, i + 4));
  }

  if (parts.length) {
    return parts.join(' ');
  } else {
    return value;
  }
}
export function dd_format(value: string, previousValue: string = '') {
  // Remove all non-numeric characters
  let v = value.replace(/[^0-9]/gi, '');

  // If empty, return empty
  if (!v) return '';

  // Get first digit
  const firstDigit = parseInt(v[0]);

  // If first digit is 2-9, prepend 0 and add separator
  if (v.length === 1 && firstDigit >= 2 && firstDigit <= 9) {
    return '0' + v + '/';
  }

  // If we have at least 2 digits
  if (v.length >= 2) {
    let month = v.substring(0, 2);
    let monthNum = parseInt(month);
    let year = '';

    // If first digit is 0 or 1 but month > 12, reformat
    // e.g., "15" becomes "01/5", "19" becomes "01/9"
    if (monthNum > 12) {
      month = '0' + v[0];
      // Rest of the digits become the year
      year = v.substring(1, 3);
      return month + '/' + year;
    }

    // If we only have 2 digits (month only)
    if (v.length === 2) {
      // Auto-add separator after valid month when typing forward
      if (
        monthNum >= 1 &&
        monthNum <= 12 &&
        previousValue.length < value.length
      ) {
        return month + '/';
      }
      return month;
    }

    // Format as MM/YY (3 or more digits)
    year = v.substring(2, 4);
    return month + '/' + year;
  }

  return v;
}

type AddressEntity = Address & {id: number};
type FormData = Omit<createCard, 'label'>;

type UserAddressesProps = {
  userId?: string;
  addresses: AddressEntity[];
  onChange?: (valud: AddressEntity[]) => void;
  setAddressModalVisible: (val: boolean) => void;
};

export type CardInformationRef = {
  addCard: () => void;
};

const CardInformation: React.ForwardRefRenderFunction<
  CardInformationRef,
  UserAddressesProps
> = ({addresses, userId, setAddressModalVisible}, ref) => {
  const {currentUser} = useAuth();
  const {createNewCard, deleteCard, cards} = useUserForm();

  const [openModal, setOpenModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeCard, setActiveCard] = useState(null);
  const [isPrimary, setIsPrimary] = useState(false);
  const handleOrdersClose = () => setOpenModal(false);
  const {push} = useHistory();

  const form = useForm<FormData>({reValidateMode: 'onChange'});
  const stripePromise = useRef<any>(null);

  useImperativeHandle(ref, () => ({
    addCard: () => addCard(),
  }));

  const addCard = () => {
    if (!get(currentUser, 'company.payment_integration')) {
      toast.error('Please, setup payment integration to add card');
      push('/settings/integrations', {
        show_modal: true,
      });
      return;
    }
    setOpenModal(true);
  };

  const handleCreation = useCallback(
    async (data: any) => {
      try {
        const card = {
          card: {
            number: data.card_number.replace(/\s+/g, ''),
            cvc: data.secure_code,
            exp_month: data.expiry_date.split('/')[0],
            exp_year: '20' + data.expiry_date.split('/')[1],
          },
          object: 'token',
          token_type: 'supt',
        };
        const public_key = get(
          currentUser,
          'company.payment_integration.public_key'
        );
        if (!public_key) {
          return toast.error('Please, setup payment integration');
        }
        setLoading(true);
        const {data: response} = await axios.post(
          `https://api.heartlandportico.com/SecureSubmit.v1/api/token?api_key=${public_key}`,
          card
        );
        const body = {
          billing_address: data.billing_address.value,
          customer: Number(userId),
          card_type: getCardType(data.card_number),
          card_number: response.card.number,
          token: response.token_value,
          cardholder_name: data.holder_name,
          is_primary: isPrimary,
        };
        await createNewCard(body);
        setLoading(false);
        setOpenModal(false);
        form.reset();
      } catch (error) {
        const message =
          get(error, 'response.data.error.message') ||
          'Something wrong with creation. Try again later !';
        toast.error(message);
        setLoading(false);
      }
    },
    [currentUser, userId, isPrimary, createNewCard, form]
  );

  const onDelete = (item: any) => {
    setActiveCard(item);
    setDeleteModal(true);
  };

  const deleteCardHandler = async () => {
    setLoading(true);
    try {
      await deleteCard(get(activeCard, 'id'));
      setLoading(false);
      setDeleteModal(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      !stripePromise.current &&
      get(currentUser, 'company.payment_integration.gateway_type') === 'stripe'
    ) {
      stripePromise.current = loadStripe(
        get(currentUser, 'company.payment_integration.public_key', '')
      );
    }
  }, [currentUser]);

  const renderForm = useCallback(() => {
    if (
      get(currentUser, 'company.payment_integration.gateway_type') === 'stripe'
    ) {
      return (
        <Elements stripe={stripePromise.current}>
          <StripeForm
            onClose={() => setOpenModal(false)}
            addresses={addresses}
            customer={userId}
            addAddress={() => setAddressModalVisible(true)}
            createNewCard={createNewCard}
          />
        </Elements>
      );
    }
    if (
      get(currentUser, 'company.payment_integration.gateway_type') ===
      'heartland'
    ) {
      return (
        <HeartlandForm
          onSuccess={handleCreation}
          addresses={addresses}
          onAddAddress={() => setAddressModalVisible(true)}
          isPrimary={isPrimary}
          setIsPrimary={setIsPrimary}
          loading={loading}
          form={form}
        />
      );
    }
  }, [
    currentUser,
    addresses,
    userId,
    createNewCard,
    setAddressModalVisible,
    handleCreation,
    isPrimary,
    loading,
    form,
  ]);

  return (
    <div className={styles.accordion}>
      <div>
        <Typography variant="h3" className={styles.accordion__title}>
          Card Information
        </Typography>

        <div className={styles.accordion__inner}>
          {cards.map((card) => (
            <div className={styles.accordion__item} key={get(card, 'id')}>
              <div className={styles.card}>
                <div className={styles.accordion__item_header}>
                  <div>{get(card, 'cardholder_name', '')} </div>
                  <img
                    className={styles.close_icon}
                    alt="close"
                    src={closeIcon}
                    onClick={() => onDelete(card)}
                  />
                </div>
                <div className={styles.row}>
                  <div>**** **** **** {get(card, 'last4', '')}</div>

                  <div className={styles.cardTypeIconWrapper}>
                    {get(card, 'type') && <CreditCardType type={card.type} />}
                  </div>
                </div>
                <div className={styles.row}>
                  <div>
                    Created Date: &nbsp;
                    {format(parseISO(card.created_at), 'MM/dd/yyyy')}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Button className={styles.addCardButton} onClick={addCard}>
        Add New Card
      </Button>
      {openModal && (
        <Modal
          isOpen={openModal}
          onClose={handleOrdersClose}
          className={styles.list_wrapper}
        >
          {renderForm()}
        </Modal>
      )}
      <DeleteModal
        openModal={deleteModal}
        setOpenModal={setDeleteModal}
        onDelete={deleteCardHandler}
        text="Are you sure you want to delete this card?"
        component={
          <div className={styles.accordion}>
            <div className={styles.accordion__inner}>
              <div className={styles.accordion__item}>
                <div className={styles.card}>
                  <div className={styles.accordion__item_header}>
                    <div>{get(activeCard, 'cardholder_name', '')}</div>
                  </div>
                  <div className={styles.row}>
                    <div>**** **** **** {get(activeCard, 'last4', '')}</div>
                    <div>
                      Expiration: &nbsp;
                      {get(activeCard, 'exp_month', '')}/
                      {get(activeCard, 'exp_year', '')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default forwardRef(CardInformation);
